'use client';

import { create } from 'zustand';
import { api, translateBackendCode } from '@/shared/lib/api';
import { ProjectStatus, type IndexingStatus, type Project } from '@/shared/lib/types';

export interface ProjectIndexingState extends IndexingStatus {
  isIndexing: boolean;
  /** Ficheiros do projeto ainda por indexar quando a última mensagem de
   * progresso foi emitida — vem do backend (`open_count`, ver
   * `WebSocketManager.send_indexing_progress`). `null` = desconhecido
   * (mensagem de um caminho que não o calcula). */
  openCount: number | null;
  /** true entre o clique em "cancelar" e a confirmação (WS) de que a indexação parou. */
  isCancelling: boolean;
  /** erro ao pedir o cancelamento (ex. falha de rede) — distinto de `error_message`. */
  cancelError: string | null;
  projectName?: string;
}

const createIdleProjectIndexingState = (): ProjectIndexingState => ({
  status: 'idle',
  openCount: null,
  total_documents: 0,
  processed_documents: 0,
  current_document: null,
  progress_percent: 0,
  error_message: null,
  started_at: null,
  completed_at: null,
  isIndexing: false,
  isCancelling: false,
  cancelError: null,
});

/** Estados do projeto em que ainda pode haver indexação a decorrer — a barra
 * fica de pé. Em qualquer outro, o backend já fechou o trabalho (ver
 * `reconcile_project_after_indexing`) e a barra tem de fechar com ele. */
const INDEXING_IN_PROGRESS_STATUSES: string[] = [
  ProjectStatus.INDEXING,
  ProjectStatus.CANCELLING,
];

/** Como um estado terminal do projeto se traduz no `status` local — é este
 * campo que decide `isIndexing`, e portanto a presença da barra no
 * ProgressToast. Única definição desta regra: o WebSocketProvider também a
 * usa quando chega um `project_status_updated`. */
export function indexingStatusForProjectStatus(
  projectStatus: string,
): ProjectIndexingState['status'] {
  if (projectStatus === ProjectStatus.INDEXING_ERROR) return 'error';
  // Cancelada, ou ficheiros à espera de processamento: não houve conclusão
  // nenhuma para anunciar — a indexação apenas já não está a decorrer.
  if (projectStatus === ProjectStatus.NEEDS_INDEXING) return 'idle';
  return 'completed';
}

/** Esta mensagem de progresso descreve trabalho a decorrer AGORA?
 *
 * Com `open_count` do backend a resposta é um facto, não uma dedução: `0`
 * significa que a indexação já fechou e esta mensagem é só um eco (o
 * heartbeat de meio segundo de um lote cujo `stop()` ainda não correu, ou uma
 * mensagem atrasada na rede). Sem ele — mensagens de caminhos que não o
 * calculam — resta a percentagem, e uma mensagem a 100% é o sintoma clássico
 * da barra que ficava presa.
 *
 * Mesma regra que `messageHasOpenWork` aplica às execuções de questionário. */
export function progressMessageHasOpenWork(
  openCount: number | null | undefined,
  percent: number | null | undefined,
): boolean {
  if (openCount === null || openCount === undefined) return (percent ?? 0) < 100;
  return openCount > 0;
}

export function isProjectIndexingStatus(projectStatus?: string | null): boolean {
  return Boolean(projectStatus && INDEXING_IN_PROGRESS_STATUSES.includes(projectStatus));
}

// Timeout defensivo: se nenhuma atualização WS (project_status_updated/indexing_error)
// confirmar o cancelamento entretanto (ex. NOTIFY perdido porque a réplica dona
// reiniciou), repõe o botão em vez de o deixar preso a girar indefinidamente.
const CANCEL_TIMEOUT_MS = 20000;
const cancelTimers = new Map<string, ReturnType<typeof setTimeout>>();

const clearCancelTimer = (projectId: string) => {
  const existing = cancelTimers.get(projectId);
  if (existing) clearTimeout(existing);
  cancelTimers.delete(projectId);
};

interface IndexingState extends IndexingStatus {
  isIndexing: boolean;
  projectStatusById: Record<string, ProjectIndexingState>;

  startIndexing: (sourceFolder: string, destFolder?: string) => Promise<void>;
  startProjectIndexing: (projectId: string, projectName?: string) => Promise<void>;
  cancelProjectIndexing: (projectId: string) => Promise<void>;
  refreshStatus: () => Promise<void>;
  updateStatus: (status: Partial<IndexingStatus>) => void;
  updateProjectStatus: (projectId: string, status: Partial<ProjectIndexingState>) => void;
  syncProjectFromServer: (project: Project) => void;
  getProjectStatus: (projectId: string) => ProjectIndexingState;
  clearDocuments: () => Promise<void>;
}

export const useIndexingStore = create<IndexingState>((set, get) => ({
  status: 'idle',
  total_documents: 0,
  processed_documents: 0,
  current_document: null,
  progress_percent: 0,
  error_message: null,
  started_at: null,
  completed_at: null,
  isIndexing: false,
  projectStatusById: {},

  startIndexing: async (sourceFolder: string, destFolder?: string) => {
    set({ isIndexing: true, status: 'scanning', error_message: null });
    try {
      await api.startFolderIndexing(sourceFolder, destFolder);
    } catch (error) {
      set({
        isIndexing: false,
        status: 'error',
        error_message: (error as Error).message,
      });
    }
  },

  startProjectIndexing: async (projectId: string, projectName?: string) => {
    clearCancelTimer(projectId);
    set((state) => ({
      projectStatusById: {
        ...state.projectStatusById,
        [projectId]: {
          ...(state.projectStatusById[projectId] ?? createIdleProjectIndexingState()),
          isIndexing: true,
          isCancelling: false,
          cancelError: null,
          status: 'scanning',
          error_message: null,
          projectName,
        },
      },
    }));

    try {
      await api.startIndexing(projectId);
    } catch (error) {
      set((state) => ({
        projectStatusById: {
          ...state.projectStatusById,
          [projectId]: {
            ...(state.projectStatusById[projectId] ?? createIdleProjectIndexingState()),
            isIndexing: false,
            status: 'error',
            error_message: (error as Error).message,
          },
        },
      }));
    }
  },

  cancelProjectIndexing: async (projectId: string) => {
    clearCancelTimer(projectId);
    set((state) => ({
      projectStatusById: {
        ...state.projectStatusById,
        [projectId]: {
          ...(state.projectStatusById[projectId] ?? createIdleProjectIndexingState()),
          isCancelling: true,
          cancelError: null,
        },
      },
    }));

    cancelTimers.set(
      projectId,
      setTimeout(() => {
        cancelTimers.delete(projectId);
        set((state) => ({
          projectStatusById: {
            ...state.projectStatusById,
            [projectId]: {
              ...(state.projectStatusById[projectId] ?? createIdleProjectIndexingState()),
              isCancelling: false,
            },
          },
        }));
      }, CANCEL_TIMEOUT_MS),
    );

    try {
      await api.cancelIndexing(projectId);
    } catch (error) {
      clearCancelTimer(projectId);
      set((state) => ({
        projectStatusById: {
          ...state.projectStatusById,
          [projectId]: {
            ...(state.projectStatusById[projectId] ?? createIdleProjectIndexingState()),
            isCancelling: false,
            cancelError: (error as Error).message,
          },
        },
      }));
    }
  },

  refreshStatus: async () => {
    try {
      const status = await api.getIndexingStatus();
      set({
        ...status,
        isIndexing: !['idle', 'completed', 'error'].includes(status.status),
      });
    } catch (error) {
      console.error('Failed to refresh status:', error);
    }
  },

  updateStatus: (status: Partial<IndexingStatus>) => {
    set((state) => ({
      ...state,
      ...status,
      isIndexing: status.status
        ? !['idle', 'completed', 'error'].includes(status.status)
        : state.isIndexing,
    }));
  },

  updateProjectStatus: (projectId: string, status: Partial<ProjectIndexingState>) => {
    // Qualquer atualização real do backend resolve um cancelamento pendente
    // (confirmado ou suplantado por um novo evento) — repõe o botão em vez de
    // deixar `isCancelling` preso à espera de uma mensagem que já chegou.
    clearCancelTimer(projectId);
    set((state) => {
      const current = state.projectStatusById[projectId] ?? createIdleProjectIndexingState();
      const nextStatus = status.status ?? current.status;
      const nextOpenCount = status.openCount !== undefined ? status.openCount : current.openCount;
      const statusSaysIndexing = !['idle', 'completed', 'error'].includes(nextStatus);

      return {
        projectStatusById: {
          ...state.projectStatusById,
          [projectId]: {
            ...current,
            ...status,
            openCount: nextOpenCount,
            // Um `stage` de trabalho em curso já não chega para a barra
            // existir: se o backend disser que não sobrou trabalho aberto,
            // esta mensagem é um eco de uma indexação terminada e não pode
            // voltar a abri-la (ver `progressMessageHasOpenWork`).
            isIndexing:
              statusSaysIndexing &&
              progressMessageHasOpenWork(nextOpenCount, status.progress_percent ?? current.progress_percent),
            isCancelling: false,
          },
        },
      };
    });
  },

  // Reconcilia o estado local com o que o servidor tem persistido para o
  // projeto. Deve ser chamada sempre que um projeto é lido do backend
  // (listagem, página do projeto) — é isto que torna o WebSocket uma
  // otimização de latência em vez da única fonte de verdade:
  //
  //   - a indexação que terminou enquanto o socket estava em baixo deixa de
  //     ficar presa a 100% "a processar" à espera de um refresh manual;
  //   - depois de um F5, a barra reaparece com a percentagem certa, mesmo
  //     que nenhum lote esteja ativo a emitir heartbeat (ex. tasks ainda
  //     PENDING à espera de capacidade), caso em que antes não havia
  //     mensagem nenhuma para a reconstruir.
  //
  // Canto conhecido: a indexação que arranca a seguir a um upload é
  // despoletada em segundo plano no backend (ver
  // `FilesController._auto_trigger_indexing_after_upload`), pelo que há uma
  // janela curta em que já chegou a primeira mensagem de progresso mas o
  // projeto ainda está NEEDS_INDEXING. Um refetch nessa janela fecha a barra
  // e a mensagem seguinte volta a abri-la — um piscar, não um estado preso.
  // Evitá-lo exigiria uma janela temporal ("ignorar o servidor durante N
  // segundos"), exatamente o tipo de heurística que o `open_count` veio
  // substituir no fluxo dos questionários (ver questionnaireRunStore).
  syncProjectFromServer: (project: Project) => {
    const projectId = String(project.project_id);
    const serverSaysIndexing = isProjectIndexingStatus(project.status);

    set((state) => {
      const current = state.projectStatusById[projectId];

      if (!serverSaysIndexing) {
        // Nada a fechar: não criamos entrada para um projeto que este cliente
        // nunca viu indexar (senão qualquer listagem enchia a store).
        if (!current?.isIndexing) return state;
        return {
          projectStatusById: {
            ...state.projectStatusById,
            [projectId]: {
              ...current,
              status: indexingStatusForProjectStatus(String(project.status)),
              isIndexing: false,
              isCancelling: false,
              projectName: project.name ?? current.projectName,
            },
          },
        };
      }

      const base = current ?? createIdleProjectIndexingState();
      // `Math.max` porque uma mensagem WebSocket pode ser mais recente do que
      // a leitura REST que a originou; a percentagem global do backend nunca
      // retrocede dentro da mesma fase (ver bump_indexing_progress), por isso
      // ficar com o maior dos dois nunca inventa progresso a mais.
      const percent = Math.max(base.progress_percent ?? 0, project.indexing_progress_percent ?? 0);
      const message = project.indexing_progress_code
        ? translateBackendCode(
            project.indexing_progress_code,
            (project.indexing_progress_params ?? {}) as Record<string, unknown>,
          )
        : base.current_document;

      return {
        projectStatusById: {
          ...state.projectStatusById,
          [projectId]: {
            ...base,
            // O `stage` persistido é a mesma string que vem nas mensagens de
            // progresso (`scanning`, `extracting`, `waiting_replica_capacity`,
            // …) — mais valores do que o union declarado, tal como já
            // acontece com o que chega por WebSocket; nenhum deles é terminal,
            // e é `isIndexing` que decide a presença da barra.
            status: (project.indexing_progress_stage ??
              (base.isIndexing ? base.status : 'scanning')) as ProjectIndexingState['status'],
            // O REST não traz contagem de trabalho aberto; o que traz é o
            // `status` do projeto, e esse já disse que está a indexar. Repor
            // a `null` evita que um `open_count: 0` de um eco antigo faça a
            // mensagem SEGUINTE fechar a barra outra vez.
            openCount: null,
            progress_percent: percent,
            current_document: message,
            isIndexing: true,
            projectName: project.name ?? base.projectName,
          },
        },
      };
    });

    if (!serverSaysIndexing) clearCancelTimer(projectId);
  },

  getProjectStatus: (projectId: string) =>
    get().projectStatusById[projectId] ?? createIdleProjectIndexingState(),

  clearDocuments: async () => {
    try {
      await api.clearDocuments();
      set({
        status: 'idle',
        total_documents: 0,
        processed_documents: 0,
        current_document: null,
        progress_percent: 0,
        error_message: null,
        started_at: null,
        completed_at: null,
        isIndexing: false,
      });
    } catch (error) {
      set({ error_message: (error as Error).message });
    }
  },
}));
